/**
 * Toasts and confirm dialogs for the page builder screens (the editor, the
 * Pages list, the Domains screen). Orbit's FeedbackProvider without
 * framer-motion or i18n: CSS animations from editor.css, reduced motion
 * respected there.
 *
 * API (keep it stable, the Pages list and Domains screens use it too):
 *
 *   <FeedbackProvider>{screen}</FeedbackProvider>
 *     Mount once per screen. Toasts and dialogs render in a portal on
 *     document.body, inside a pe-scope wrapper, above every NL-AOS layer.
 *
 *   const toast = useToast();
 *   toast(message, kind = 'success', action?)
 *     kind: 'success' | 'error' | 'info'. action: { label, onAction } adds a
 *     button (Undo, Reload). Shows 3.2s, or 6s with an action or for an error.
 *
 *   const confirm = useConfirm();
 *   const ok = await confirm({ title, body?, confirmLabel?, danger?, requireText? });
 *     Resolves true on the confirm button (or Enter), false on Cancel, Esc or a
 *     click outside. danger draws the confirm button red. requireText keeps
 *     the confirm button off until that exact text is typed.
 *
 * Outside a provider, toast does nothing and confirm falls back to the
 * browser's own dialog.
 */
import { createContext, useCallback, useContext, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { CheckIcon, XIcon } from '../icons';

export type ToastKind = 'success' | 'error' | 'info';
export interface ToastAction { label: string; onAction: () => void }
export type ToastFn = (message: string, kind?: ToastKind, action?: ToastAction) => void;

export interface ConfirmOptions {
  title: string;
  body?: string;
  /** The confirm button's words. Default "Confirm". */
  confirmLabel?: string;
  /** A destructive action: the confirm button is red. */
  danger?: boolean;
  /** The confirm button stays off until this exact text is typed. */
  requireText?: string;
}
export type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ToastContext = createContext<ToastFn>(() => {});
const ConfirmContext = createContext<ConfirmFn>(async (o) =>
  typeof window !== 'undefined' && typeof window.confirm === 'function' ? !!window.confirm(o.body ? `${o.title}\n\n${o.body}` : o.title) : false);

export const useToast = () => useContext(ToastContext);
export const useConfirm = () => useContext(ConfirmContext);

/** How long a toast stays (ms). */
export const TOAST_MS = 3200;
export const TOAST_LONG_MS = 6000;
const LEAVE_MS = 160;

interface Toast { id: number; message: string; kind: ToastKind; action?: ToastAction; leaving?: boolean }
interface ConfirmState extends ConfirmOptions { resolve: (ok: boolean) => void }

/** The info toast's "i" mark, drawn like the other icons (24x24, 1.75 stroke). */
function InfoMark({ size = 13 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 11v6M12 7h.01" />
    </svg>
  );
}

export function FeedbackProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const seq = useRef(0);
  const timers = useRef(new Set<ReturnType<typeof setTimeout>>());
  useEffect(() => () => { timers.current.forEach(clearTimeout); timers.current.clear(); }, []);
  const later = useCallback((fn: () => void, ms: number) => {
    const t = setTimeout(() => { timers.current.delete(t); fn(); }, ms);
    timers.current.add(t);
  }, []);

  const dismiss = useCallback((id: number) => {
    setToasts((all) => all.map((t) => (t.id === id ? { ...t, leaving: true } : t)));
    later(() => setToasts((all) => all.filter((t) => t.id !== id)), LEAVE_MS);
  }, [later]);

  const toast = useCallback<ToastFn>((message, kind = 'success', action) => {
    const id = ++seq.current;
    setToasts((all) => [...all, { id, message, kind, action }]);
    later(() => dismiss(id), action || kind === 'error' ? TOAST_LONG_MS : TOAST_MS);
  }, [later, dismiss]);

  const [confirmState, setConfirmState] = useState<ConfirmState | null>(null);
  const pending = useRef<ConfirmState | null>(null);
  useEffect(() => () => {
    // Navigating away dismisses the dialog as well as its waiting action.
    // No caller should remain suspended after the provider has gone away.
    const cur = pending.current;
    pending.current = null;
    cur?.resolve(false);
  }, []);
  const confirm = useCallback<ConfirmFn>((options) => new Promise<boolean>((resolve) => {
    pending.current?.resolve(false); // a second confirm replaces the first, which counts as cancelled
    const next = { ...options, resolve };
    pending.current = next;
    setConfirmState(next);
  }), []);
  const settle = useCallback((ok: boolean) => {
    const cur = pending.current;
    pending.current = null;
    setConfirmState(null);
    cur?.resolve(ok);
  }, []);

  return (
    <ToastContext.Provider value={toast}>
      <ConfirmContext.Provider value={confirm}>
        {children}
        {typeof document !== 'undefined' && createPortal(
          <div className="pe-scope">
            <div className="pe-toast-host" data-toast-host>
              {toasts.map((t) => (
                <div key={t.id} className="pe-toast" role={t.kind === 'error' ? 'alert' : 'status'} aria-live={t.kind === 'error' ? 'assertive' : 'polite'}
                  data-kind={t.kind} data-leaving={t.leaving || undefined}>
                  <span className={`pe-toast-icon pe-toast-icon--${t.kind}`} aria-hidden="true">
                    {t.kind === 'error' ? <XIcon size={13} strokeWidth={3} /> : t.kind === 'info' ? <InfoMark /> : <CheckIcon size={13} strokeWidth={3} />}
                  </span>
                  <span className="pe-toast-msg">{t.message}</span>
                  {t.action && (
                    <button type="button" className="pe-toast-action"
                      onClick={() => { t.action!.onAction(); dismiss(t.id); }}>{t.action.label}</button>
                  )}
                </div>
              ))}
            </div>
            {confirmState && <ConfirmDialog state={confirmState} onSettle={settle} />}
          </div>,
          document.body,
        )}
      </ConfirmContext.Provider>
    </ToastContext.Provider>
  );
}

function ConfirmDialog({ state, onSettle }: { state: ConfirmState; onSettle: (ok: boolean) => void }) {
  const [text, setText] = useState('');
  const titleId = useId();
  const bodyId = useId();
  const okRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const textOk = !state.requireText || text.trim() === state.requireText;

  // Focus moves in once and back once. Esc cancels before anything under the
  // dialog (a drawer, the editor's selection) sees it.
  useEffect(() => {
    const back = document.activeElement as HTMLElement | null;
    (state.requireText ? inputRef.current : okRef.current)?.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      onSettle(false);
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      if (back?.isConnected && back !== document.body) back.focus?.({ preventScroll: true });
    };
  }, [state, onSettle]);

  return (
    <>
      <div className="pe-dialog-scrim" aria-hidden="true" onMouseDown={() => onSettle(false)} />
      <div className="pe-dialog-wrap">
        <div role="alertdialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={state.body ? bodyId : undefined}
          className="pe-dialog" data-confirm>
          <h2 id={titleId}>{state.title}</h2>
          {state.body && <p id={bodyId} className="pe-dialog-body">{state.body}</p>}
          {state.requireText && (
            <input ref={inputRef} className="input mt-3" value={text} aria-label={`Type ${state.requireText} to confirm`}
              placeholder={`Type “${state.requireText}” to confirm`} autoComplete="off" spellCheck={false}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && textOk) { e.preventDefault(); onSettle(true); } }} />
          )}
          <div className="pe-dialog-actions">
            <button type="button" className="btn-ghost" onClick={() => onSettle(false)}>Cancel</button>
            <button ref={okRef} type="button" className={state.danger ? 'btn-danger' : 'btn-primary'} disabled={!textOk}
              onClick={() => onSettle(true)}>{state.confirmLabel ?? 'Confirm'}</button>
          </div>
        </div>
      </div>
    </>
  );
}
